import React from 'react';
import Select, { caseInsensitiveMatch, getSearchOptions } from '../Select';
import { act } from 'react-dom/test-utils';
import { render } from '@testing-library/react';
import { unmountComponentAtNode } from 'react-dom';

describe('Select', () => {
  const container = document.createElement('div');
  const selectRef = React.createRef();

  describe('validateInputValue', () => {
    it('should return true if Select is clearable and given value is an empty string', () => {
      act(() => {
        render(<Select ref={selectRef} clearable />, container);
      });

      expect(selectRef.current.validateInputValue('')).toBe(true);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return false if Select is clearable and given value null or undefined', () => {
      act(() => {
        render(<Select ref={selectRef} clearable />, container);
      });

      expect(selectRef.current.validateInputValue(null)).toBe(false);
      expect(selectRef.current.validateInputValue(undefined)).toBe(false);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return true if Select is clearable and given object containing value that is an empty string', () => {
      act(() => {
        render(
          <Select valueKey="value" clearable ref={selectRef} />,
          container
        );
      });
      expect(selectRef.current.validateInputValue({ value: '' })).toBe(true);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return false if Select is clearable and given object containing value that is null or undefined', () => {
      act(() => {
        render(
          <Select valueKey="value" clearable ref={selectRef} />,
          container
        );
      });
      expect(selectRef.current.validateInputValue({ value: null })).toBe(false);
      expect(selectRef.current.validateInputValue({ value: undefined })).toBe(
        false
      );
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('shoud return true if given value string matches an option', () => {
      act(() => {
        render(
          <Select
            clearable
            options={['John', 'Paul', 'George']}
            ref={selectRef}
          />,
          container
        );
      });
      expect(selectRef.current.validateInputValue('George')).toBe(true);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('shoud return false if given value string does not match an option', () => {
      act(() => {
        render(
          <Select
            clearable
            options={['John', 'Paul', 'George']}
            ref={selectRef}
          />,
          container
        );
      });
      expect(selectRef.current.validateInputValue('Ringo')).toBe(false);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return true if given value object matches an option', () => {
      act(() => {
        render(
          <Select
            clearable
            options={[
              { label: 'John', value: 'john' },
              { label: 'Paul', value: 'paul' },
              { label: 'George', value: 'george' },
            ]}
            valueKey="value"
            ref={selectRef}
          />,
          container
        );
      });
      expect(
        selectRef.current.validateInputValue({
          label: 'George',
          value: 'george',
        })
      ).toBe(true);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return false if given value object does not match an option', () => {
      act(() => {
        render(
          <Select
            clearable
            options={[
              { label: 'John', value: 'john' },
              { label: 'Paul', value: 'paul' },
              { label: 'George', value: 'george' },
            ]}
            valueKey="value"
            ref={selectRef}
          />,
          container
        );
      });
      expect(
        selectRef.current.validateInputValue({ label: 'Ringo', value: 'ringo' })
      ).toBe(false);
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return true if given string value matched object value', () => {
      act(() => {
        render(
          <Select
            clearable
            options={[
              { label: 'John', value: 'john' },
              { label: 'Paul', value: 'paul' },
              { label: 'George', value: 'george' },
            ]}
            valueKey="value"
            ref={selectRef}
          />,
          container
        );
      });
      expect(selectRef.current.validateInputValue('george')).toBe(true);
      act(() => {
        unmountComponentAtNode(container);
      });
    });
  });

  describe('getFallbackValueOnChange', () => {
    it('should return null if Select is clearble', () => {
      act(() => {
        render(<Select clearable value="George" ref={selectRef} />, container);
      });
      expect(selectRef.current.getFallbackValueOnChange()).toBeNull();
      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('should return given prop value if Select is not clearable', () => {
      act(() => {
        render(<Select value="George" ref={selectRef} />, container);
      });
      expect(selectRef.current.getFallbackValueOnChange()).toEqual('George');
      act(() => {
        unmountComponentAtNode(container);
      });
    });
  });

  describe('getSearchOptions', () => {
    it('should return all options if search is undefined', () => {
      const options = ['John', 'Paul', 'George'];
      const searchValue = undefined;
      expect(getSearchOptions(options, searchValue)).toStrictEqual(['John', 'Paul', 'George']);
    });

    it('should return all options if search is null', () => {
      const options = ['John', 'Paul', 'George'];
      const searchValue = null;
      expect(getSearchOptions(options, searchValue)).toStrictEqual(['John', 'Paul', 'George']);
    });
    
    it('should return all options if search is empty string', () => {
      const options = ['John', 'Paul', 'George'];
      const searchValue = '';
      expect(getSearchOptions(options, searchValue)).toStrictEqual(['John', 'Paul', 'George']);
    });
    
    it('should return empty result array if search does not match', () => {
      const options = ['John', 'Paul', 'George'];
      const searchValue = 'Jane';
      expect(getSearchOptions(options, searchValue)).toStrictEqual([]);
    });

    it('should return filtered string options if search is not empty', () => {
      const options=['John', 'Paul', 'George'];
      const searchValue = 'Geo';
      expect(getSearchOptions(options, searchValue)).toStrictEqual(['George']);
    });
    
    it('should return filtered object options regardless of case if search is not empty', () => {
      const options=[
        { label: 'John', value: 'john', instrument: 'guitar' },
        { label: 'Paul', value: 'paul', instrument: 'bass' },
        { label: 'George', value: 'george', instrument: 'guitar' },
      ];
      const searchValue = 'geo';
      expect(getSearchOptions(options, searchValue)).toStrictEqual([{ label: 'George', value: 'george', instrument: 'guitar' }]);
    });
    
    it('should return filtered string options regardless of case when the search is not empty', () => {
      const options = ['John', 'Paul', 'George'];
      const searchValue = 'geo';
      expect(getSearchOptions(options, searchValue)).toEqual(['George']);
    });
    
    it('should return filtered object options if search is not empty', () => {
      const options = [
        { label: 'John', value: 'john', instrument: 'guitar' },
        { label: 'Paul', value: 'paul', instrument: 'bass' },
        { label: 'George', value: 'george', instrument: 'guitar' },
      ];
      const searchValue = 'guitar';
      expect(getSearchOptions(options, searchValue)).toEqual([
        { label: 'John', value: 'john', instrument: 'guitar' },
        { label: 'George', value: 'george', instrument: 'guitar' },
      ]);
    });
  });
  describe('caseInsensitiveMatch', () => {
    it('should return true if given searchValue is case insensitive full match of given option', () => {
      const option = 'George';
      const searchValue = 'georGe';
      expect(caseInsensitiveMatch(option, searchValue)).toBe(true);
    });

    it('should return true if given searchValue is case insensitive partial match of given option', () => {
      const option = 'George';
      const searchValue = 'geo';
      expect(caseInsensitiveMatch(option, searchValue)).toBe(true);
    });

    it('should return false if searchValue does not match option', () => {
      const option = 'George';
      const searchValue = 'John';
      expect(caseInsensitiveMatch(option, searchValue)).toBe(false);
    });

    it('should return false if option is partial match of search', () => {
      const option = 'Billy';
      const searchValue = 'BillyJeans';
      expect(caseInsensitiveMatch(option, searchValue)).toBe(false);
    });
  });
});
